import { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../services/config';
import { fetchAllProducts } from '../../services/productsApi';

interface SyncStatus {
  lastSync: Date | null;
  syncing: boolean;
  productCount: number;
  connected: boolean;
  error: string | null;
}

export default function AdminPanel() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SyncStatus>({
    lastSync: null,
    syncing: false,
    productCount: 0,
    connected: false,
    error: null,
  });

  const checkStatus = useCallback(async () => {
    setStatus((s) => ({ ...s, syncing: true, error: null }));
    try {
      const products = await fetchAllProducts({ pageSize: 1 });
      setStatus({
        lastSync: new Date(),
        syncing: false,
        productCount: products.length,
        connected: true,
        error: null,
      });
    } catch {
      setStatus((s) => ({
        ...s,
        syncing: false,
        connected: false,
        error: 'No se pudo conectar con InnoFlex',
      }));
    }
  }, []);

  useEffect(() => {
    if (open) {
      checkStatus();
    }
  }, [open, checkStatus]);

  return (
    <>
      {/* Floating gear button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] w-12 h-12 flex items-center justify-center rounded-full bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white hover:border-red-500/50 transition-all duration-300 cursor-pointer shadow-lg"
        title="Panel de Administración"
        aria-label="Panel de Administración"
      >
        <i className={`ri-settings-3-line text-xl transition-transform duration-500 ${open ? 'rotate-90' : ''}`} />
      </button>

      {/* Admin Drawer */}
      {open && (
        <div className="fixed inset-0 z-[59] flex justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div className="relative w-full max-w-sm bg-zinc-950 border-l border-zinc-800 h-full flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-600/10 border border-red-600/20">
                  <i className="ri-settings-3-line text-red-500 text-lg" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Admin SpartechRD</h3>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Sincronización InnoFlex</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
                aria-label="Cerrar panel"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Connection status */}
              <div className="space-y-3">
                <h4 className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Estado de Conexión</h4>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">Servidor InnoFlex</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${status.connected ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <span className={`text-xs font-medium ${status.connected ? 'text-emerald-400' : 'text-red-400'}`}>
                        {status.connected ? 'Conectado' : 'Desconectado'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">URL Base</span>
                    <span className="text-zinc-300 text-xs font-mono truncate max-w-[140px]">{API_CONFIG.BASE_URL}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">Company ID</span>
                    <span className="text-zinc-300 text-xs font-mono">{API_CONFIG.COMPANY_ID}</span>
                  </div>
                </div>
              </div>

              {/* Sync status */}
              <div className="space-y-3">
                <h4 className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Sincronización</h4>
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">Última sincronización</span>
                    <span className="text-zinc-300 text-xs">
                      {status.lastSync
                        ? status.lastSync.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                        : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-xs">Productos activos</span>
                    <span className="text-zinc-300 text-xs font-semibold">{status.productCount}</span>
                  </div>
                  {status.error && (
                    <div className="flex items-start gap-2 text-red-400 text-xs bg-red-500/10 rounded-lg p-2.5">
                      <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                      {status.error}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <h4 className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Acciones</h4>
                <button
                  onClick={checkStatus}
                  disabled={status.syncing}
                  className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-xs font-bold uppercase tracking-wider py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {status.syncing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sincronizando...
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line" />
                      Forzar Sincronización
                    </>
                  )}
                </button>
                <a
                  href="/products"
                  onClick={(e) => { e.preventDefault(); setOpen(false); window.location.href = '/products'; }}
                  className="w-full flex items-center justify-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white text-xs font-bold uppercase tracking-wider py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shopping-bag-3-line" />
                  Ver Catálogo
                </a>
              </div>

              {/* Info */}
              <div className="pt-4 border-t border-zinc-800">
                <p className="text-zinc-500 text-[10px] leading-relaxed text-center">
                  Los productos se sincronizan automáticamente desde InnoFlex cada 30 segundos.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
